import { ArrowLeft, ArrowUpRight, BarChart3, Bell, Bold, BookmarkPlus, CheckCircle, ChevronLeft, ChevronRight, Circle as CircleIcon, Clock3, Crop, Download, Euro, Eye, EyeOff, FileText, Folder, GitBranch, GitMerge, History, ImagePlus, Italic, MessageSquare, Pencil, Plus, RefreshCw, RotateCcw, RotateCw, Search, Sparkles, Square, Trash2, Type, Undo2, Upload, Users, Wand2, X, Zap } from "lucide-react";
import katex from "katex";
import "katex/dist/katex.min.css";
import { Link, useParams } from "react-router-dom";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, streamGuttenbergEvents } from "./api";
import { useAuth } from "../Auth";
import "./Guttenberg.css";
import { DriveSourcePicker } from "./GuttenbergWorkflowPanels";
import { assetThumbnailSrc, markdownImageRefs, reportImageSrc } from "./GuttenbergImageAndDiff";
import { buildRichPlanSections, linesFromText, sourceContextDocumentType, sourceImageExclusionSuggestion, sourcePathLabel, sourceStatusLabel, visualAssetSourceKey, visualAssetSourceLabel } from "./GuttenbergPageUtils";

export function AnalysisPanel({ analysis, draft, setDraft, onSave, onVisualStatusChange, onVisualLabelChange, projectId, project, busy, visualEditingLocked, report, titleDraft, onTitleChange, targetPages, onTargetPagesChange, contextDraft, onContextChange, onSaveBrief, onUploadFiles, onUploadClientLogo, driveProps, showDrive, onPrepareVisuals, visualPreparationRunning, visualInventoryReady, visualInventoryStale, onAnalyze, onResetAnalysis, analysisRunning, hasAnalysisArtifacts, operationInProgress, intakeEvents = [], job, progress, status, onCancel, chatMessages = [], chatMessage, onChatMessageChange, onSendChat }) {
    const charts = analysis?.chart_candidates ?? [];
    const images = (analysis?.visual_assets ?? []).filter((asset) => asset.type !== "chart");
    const documentLabels = analysis?.document_labels ?? [];
    const labeledSourceIds = new Set(documentLabels.map((label) => label.source_file_id));
    const sourceRows = [
        ...documentLabels,
        ...(project?.source_files ?? [])
            .filter((source) => !labeledSourceIds.has(source.id))
            .map((source) => ({
            id: source.id,
            source_file_id: source.id,
            filename: source.filename,
            source_path: sourcePathLabel(source),
            document_type: source.origin === "deepflow_snapshot" ? "snapshot Deepflow" : "document",
            description: source.parsed ? "Source analysee, sans libelle dedie." : "Source disponible, pas encore analysee.",
            relevance: "",
            key_results: [],
            limitations: [],
            status: source.parse_status || "pending"
        }))
    ];
    const [detailTab, setDetailTab] = useState("findings");
    const [setupOpen, setSetupOpen] = useState(!(visualInventoryReady || hasAnalysisArtifacts));
    const setupCollapsedByResultsRef = useRef(false);
    useEffect(() => {
        if ((visualInventoryReady || hasAnalysisArtifacts) && !setupCollapsedByResultsRef.current) {
            setupCollapsedByResultsRef.current = true;
            setSetupOpen(false);
        }
    }, [visualInventoryReady, hasAnalysisArtifacts]);
    const [openSourceId, setOpenSourceId] = useState(null);
    const [sourcePreviews, setSourcePreviews] = useState({});
    const [sourcePreviewErrors, setSourcePreviewErrors] = useState({});
    const [loadingSourceId, setLoadingSourceId] = useState(null);
    const [sourceExclusionPrompt, setSourceExclusionPrompt] = useState(null);
    const [rejectAllPrompt, setRejectAllPrompt] = useState(false);
    const dismissedSourceSuggestionsRef = useRef(new Set());
    function sourceImages(asset) {
        const sourceKey = visualAssetSourceKey(asset);
        if (!sourceKey)
            return [];
        return images.filter((candidate) => visualAssetSourceKey(candidate) === sourceKey);
    }
    async function rejectSourceImages(asset) {
        const relatedImages = sourceImages(asset);
        if (relatedImages.length < 2) {
            await onVisualStatusChange(asset.id, "rejected");
            return;
        }
        await onVisualStatusChange(asset.id, "rejected", { scope: "source" });
    }
    async function changeVisualStatus(asset, nextStatus) {
        await onVisualStatusChange(asset.id, nextStatus);
        if (nextStatus !== "rejected")
            return;
        const suggestion = sourceImageExclusionSuggestion(asset, images);
        if (
            suggestion.shouldSuggest
            && !dismissedSourceSuggestionsRef.current.has(suggestion.sourceKey)
        ) {
            setSourceExclusionPrompt({
                assetId: asset.id,
                sourceKey: suggestion.sourceKey,
                sourceLabel: visualAssetSourceLabel(asset),
                totalCount: suggestion.totalCount,
                remainingCount: suggestion.remainingCount,
                trigger: "repeated"
            });
        }
    }
    function dismissSourceExclusionPrompt(remember = false) {
        if (remember && sourceExclusionPrompt?.sourceKey)
            dismissedSourceSuggestionsRef.current.add(sourceExclusionPrompt.sourceKey);
        setSourceExclusionPrompt(null);
    }
    async function rejectPromptSourceImages() {
        if (!sourceExclusionPrompt)
            return;
        const prompt = sourceExclusionPrompt;
        setSourceExclusionPrompt(null);
        await onVisualStatusChange(prompt.assetId, "rejected", { scope: "source" });
    }
    async function rejectAllImages() {
        const firstImage = images.find((asset) => asset.status !== "rejected");
        setRejectAllPrompt(false);
        if (!firstImage)
            return;
        await onVisualStatusChange(firstImage.id, "rejected", { scope: "all" });
    }
    async function toggleSourcePreview(label) {
        const sourceId = label.source_file_id;
        if (!sourceId)
            return;
        if (openSourceId === sourceId) {
            setOpenSourceId(null);
            return;
        }
        setOpenSourceId(sourceId);
        if (sourcePreviews[sourceId] || loadingSourceId === sourceId)
            return;
        setLoadingSourceId(sourceId);
        setSourcePreviewErrors((current) => ({ ...current, [sourceId]: "" }));
        try {
            const preview = await api.sourcePreview(projectId, sourceId);
            setSourcePreviews((current) => ({ ...current, [sourceId]: preview }));
        }
        catch (error) {
            setSourcePreviewErrors((current) => ({
                ...current,
                [sourceId]: error.message || "Impossible de charger la source."
            }));
        }
        finally {
            setLoadingSourceId(null);
        }
    }
    const sourceCount = project?.source_files?.length ?? 0;
    const findingsCount = (analysis?.findings.length || analysis?.useful_facts.length) ?? 0;
    const visiblePlanSections = ["draft", "stale", "approved"].includes(analysis?.outline_stage ?? "")
        ? buildRichPlanSections(analysis, report?.manifest)
        : [];
    const imageCount = images.length;
    const briefReady = (contextDraft ?? "").trim().length > 0;
    const canPrepareVisuals = sourceCount > 0 && !operationInProgress;
    const canAnalyze = sourceCount > 0 && visualInventoryReady && !operationInProgress;
    const hasProgressTotal = Boolean(job?.progress_total);
    const liveSteps = intakeEvents.slice(-8);
    const detailTabs = [
        { id: "findings", label: "Synthèse", count: findingsCount },
        { id: "data", label: "Données", count: (analysis?.generated_artifacts.length || analysis?.data_artifacts.length || 0) + charts.length },
        { id: "plan", label: "Plan", count: visiblePlanSections.length },
        { id: "sources", label: "Sources", count: sourceRows.length },
        { id: "runs", label: "Passes", count: (analysis?.analysis_tasks.length ?? 0) + (analysis?.agent_runs.length ?? 0) },
        { id: "brief", label: "Brief affiné", count: draft.analysis_brief.trim() || draft.report_intent.objective.trim() ? 1 : 0 }
    ];
    return (<div className="analysis-flow">
      <section className="analysis-stage setup-stage">
        <button className="stage-head" type="button" onClick={() => setSetupOpen((current) => !current)} aria-expanded={setupOpen}>
          <div className="stage-head-title">
            <span className="stage-badge">1</span>
            <div>
              <h3>Préparer l'analyse</h3>
              <p className="muted">Brief, longueur cible et sources : tout part d'ici.</p>
            </div>
          </div>
          <span className="stage-toggle">{setupOpen ? "Réduire" : "Modifier"}</span>
        </button>
        {setupOpen && (<div className="setup-body">
          <div className="setup-grid">
            <div className="setup-col">
              <div className="setup-col-head">
                <span className="step-chip">Brief</span>
                <button className="button secondary compact-button" type="button" disabled={busy} onClick={onSaveBrief}>Enregistrer</button>
              </div>
              <div className="field">
                <label htmlFor="analysis-report-title">Titre du rapport</label>
                <input id="analysis-report-title" value={titleDraft} onChange={(event) => onTitleChange(event.target.value)} placeholder="Titre du rapport"/>
              </div>
              <div className="field primary-field">
                <label htmlFor="analysis-context">Objectif et contexte <em>· essentiel</em></label>
                <textarea id="analysis-context" rows={6} value={contextDraft} onChange={(event) => onContextChange(event.target.value)} placeholder="Décris ce que le rapport doit démontrer, pour qui, et le message clé attendu."/>
              </div>
              <div className="field">
                <label htmlFor="analysis-target-pages">Longueur cible</label>
                <input id="analysis-target-pages" value={targetPages} onChange={(event) => onTargetPagesChange(event.target.value)} placeholder="Optionnel, ex : 15 pages"/>
              </div>
              <div className="client-logo-inline">
                <div className="client-logo-preview small">
                  {project.client_logo_path ? (<img src={reportImageSrc(projectId, project.client_logo_path)} alt={`Logo ${project.client || "client"}`}/>) : (<span>Logo client</span>)}
                </div>
                <label className="dropzone compact-dropzone">
                  <ImagePlus size={15}/>
                  <span>{project.client_logo_path ? "Remplacer le logo" : "Ajouter un logo client"}</span>
                  <input hidden type="file" accept="image/*" onChange={onUploadClientLogo}/>
                </label>
              </div>
            </div>
            <div className="setup-col">
              <div className="setup-col-head">
                <span className="step-chip">Sources</span>
                <span className="muted">{sourceCount} fichier(s)</span>
              </div>
              <label className="dropzone row">
                <Upload size={18}/>
                <span>Glisser ou choisir des fichiers</span>
                <input hidden type="file" multiple onChange={onUploadFiles}/>
              </label>
              {showDrive && <DriveSourcePicker {...driveProps}/>}
              <ul className="file-list source-scroll">
                {(project.source_files ?? []).map((file) => (<li key={file.id}>
                    {file.filename}
                    {sourcePathLabel(file) && sourcePathLabel(file) !== file.filename && (<div className="muted">{sourcePathLabel(file)}</div>)}
                    <div className="muted">{file.origin === "deepflow_drive" ? "Drive - " : ""}{sourceStatusLabel(file)}</div>
                  </li>))}
                {!sourceCount && <li className="muted">Aucune source pour l'instant.</li>}
              </ul>
            </div>
          </div>
          <div className="setup-cta">
            <div className="setup-cta-info">
              <strong>{visualInventoryReady ? "Bibliothèque d'images prête" : visualInventoryStale ? "Actualiser les images" : "Extraire les images"}</strong>
              <span className="muted">
                {sourceCount === 0
                ? "Ajoute au moins une source pour préparer la bibliothèque."
                : !briefReady
                    ? "Astuce : renseigne l'objectif pour un meilleur résultat."
                    : visualInventoryReady
                        ? "Les images sont affichées plus bas : sélectionne, annote ou écarte-les avant l'analyse."
                        : "Guttenberg extrait les images sans lancer l'analyse ni poser de question."}
              </span>
            </div>
            <button className="button analyze-button" type="button" disabled={!canPrepareVisuals || visualInventoryReady} onClick={onPrepareVisuals} title={sourceCount === 0 ? "Ajoute au moins une source" : undefined}>
              <ImagePlus size={18}/> {visualPreparationRunning ? "Extraction en cours..." : visualInventoryReady ? "Images prêtes" : visualInventoryStale ? "Actualiser les images" : "Extraire les images"}
            </button>
          </div>
        </div>)}
      </section>

      {(analysisRunning || visualPreparationRunning || chatMessages.length > 0 || !hasAnalysisArtifacts) && (<section className="analysis-stage convo-stage">
        {(analysisRunning || visualPreparationRunning) ? (<div className="live-activity">
            <div className="live-head">
              <span className="live-pulse" aria-hidden="true"/>
              <div>
                <h3>{visualPreparationRunning ? "Guttenberg prépare les images" : "L'IA analyse votre dossier"}</h3>
                <p className="muted">{job?.message || status || (visualPreparationRunning ? "Extraction des visuels intégrés aux sources..." : "Lecture des sources et extraction des constats...")}</p>
              </div>
              {onCancel && (<button className="button secondary compact-button" type="button" onClick={onCancel}>
                  <X size={15}/> Arrêter
                </button>)}
            </div>
            <div className={`progress ${hasProgressTotal ? "" : "indeterminate"}`}><span style={{ width: `${hasProgressTotal ? progress : 40}%` }}/></div>
            {!!liveSteps.length && (<ol className="live-steps">
                {liveSteps.map((item, index) => (<li key={`${item}-${index}`}>{item}</li>))}
              </ol>)}
          </div>) : (<div className="convo-intro">
            <h3>{chatMessages.length ? "Échange avec l'IA" : "Une question, un doute ?"}</h3>
            <p className="muted">Ajoute du contexte ou réponds aux questions de l'IA. Tout passe par ce fil.</p>
          </div>)}
        <div className="chat-log compact-chat-log">
          {chatMessages.map((message) => (<div className={`chat-message ${message.role}`} key={message.id}>
              {message.content}
            </div>))}
          {!chatMessages.length && <div className="muted">Aucun message pour l'instant.</div>}
        </div>
        <form className="chat-form" onSubmit={onSendChat}>
          <input value={chatMessage} onChange={(event) => onChatMessageChange(event.target.value)} placeholder="Ajouter du contexte ou répondre à une question"/>
          <button className="button secondary" type="submit" title="Envoyer">
            <MessageSquare size={17}/>
          </button>
        </form>
      </section>)}

      {(visualInventoryReady || visualInventoryStale || visualPreparationRunning) && (<section className="analysis-stage images-stage">
        <div className="stage-head static">
          <div className="stage-head-title">
            <span className="stage-badge">2</span>
            <div>
              <h3>Sélectionner les images avant l'analyse</h3>
              <p className="muted">Conserve, impose, annote ou écarte les visuels. Les images rejetées ne seront plus proposées aux agents pour le rapport.</p>
            </div>
          </div>
          <div className="visual-library-actions">
            <span className="muted">{imageCount} image(s)</span>
            {!!images.some((asset) => asset.status !== "rejected") && (<button className="button secondary compact-button" type="button" disabled={visualEditingLocked} onClick={() => setRejectAllPrompt(true)}>
                Tout écarter puis choisir
              </button>)}
          </div>
        </div>
        {visualInventoryStale && (<div className="analysis-lock-banner warning">
            Les sources ont changé depuis cette extraction. Actualise la bibliothèque avant de lancer l'analyse.
          </div>)}
        {visualEditingLocked && (<div className="analysis-lock-banner">
            {visualPreparationRunning
                ? "Extraction en cours : la bibliothèque sera éditable dès que toutes les images auront été enregistrées."
                : "Analyse en cours : les choix d'images et les notes sont verrouillés jusqu'à la fin pour éviter les resets."}
          </div>)}
        {rejectAllPrompt && (<div className="source-image-exclusion-prompt" role="dialog" aria-label="Commencer avec aucune image sélectionnée">
            <div>
              <strong>Commencer avec aucune image sélectionnée ?</strong>
              <span>Toutes les images passeront à « refusée », mais resteront visibles et modifiables. Tu pourras ensuite conserver ou imposer uniquement celles que tu veux utiliser.</span>
            </div>
            <div className="source-image-exclusion-actions">
              <button className="button danger compact-button" type="button" onClick={rejectAllImages}>
                Tout écarter puis choisir
              </button>
              <button className="button secondary compact-button" type="button" onClick={() => setRejectAllPrompt(false)}>
                Annuler
              </button>
            </div>
          </div>)}
        {sourceExclusionPrompt && (<div className="source-image-exclusion-prompt" role="dialog" aria-label="Écarter les images du document">
            <div>
              <strong>Écarter toutes les images de « {sourceExclusionPrompt.sourceLabel} » ?</strong>
              <span>
                {sourceExclusionPrompt.trigger === "repeated"
                ? `Tu as déjà écarté plusieurs images de ce document. ${sourceExclusionPrompt.remainingCount} image(s) reste(nt) à traiter.`
                : `Ce document contient ${sourceExclusionPrompt.totalCount} image(s) détectée(s).`}
              </span>
            </div>
            <div className="source-image-exclusion-actions">
              <button className="button danger compact-button" type="button" onClick={rejectPromptSourceImages}>
                Écarter toutes ses images
              </button>
              <button className="button secondary compact-button" type="button" onClick={() => dismissSourceExclusionPrompt(sourceExclusionPrompt.trigger === "repeated")}>
                {sourceExclusionPrompt.trigger === "repeated" ? "Continuer image par image" : "Annuler"}
              </button>
            </div>
          </div>)}
        {images.length ? (<div className="asset-grid large">
            {images.map((asset) => (<AssetPreview asset={asset} projectId={projectId} key={asset.id} onStatusChange={(status) => changeVisualStatus(asset, status)} onRequestSourceRejection={() => rejectSourceImages(asset)} onLabelChange={(userLabel) => onVisualLabelChange(asset.id, userLabel)} locked={visualEditingLocked}/>))}
        </div>) : (<div className="muted empty-images">Aucune image détectée dans les sources.</div>)}
      </section>)}

      {visualInventoryReady && (<section className="analysis-stage analysis-launch-stage">
          <div className="analysis-launch-copy">
            <span className="stage-badge">3</span>
            <div>
              <h3>{hasAnalysisArtifacts ? "Reprendre l'analyse" : "Lancer l'analyse"}</h3>
              <p className="muted">
                {hasAnalysisArtifacts
                  ? "Guttenberg capitalisera sur les constats, calculs et checkpoints déjà sauvegardés, puis reprendra uniquement les phases encore utiles."
                  : "L'agent recevra maintenant les sources, tes annotations et uniquement les images non rejetées. Les questions éventuelles arriveront après cette analyse."}
              </p>
            </div>
          </div>
          <div className="analysis-launch-actions">
            <button className="button analyze-button" type="button" disabled={!canAnalyze} onClick={onAnalyze}>
              <Wand2 size={18}/> {analysisRunning ? "Analyse en cours..." : hasAnalysisArtifacts ? "Reprendre avec ces choix" : "Analyser avec ces choix"}
            </button>
            {hasAnalysisArtifacts && onResetAnalysis && (<button className="analysis-reset-link" type="button" disabled={operationInProgress} onClick={onResetAnalysis}>
                Réinitialiser l'analyse
              </button>)}
          </div>
        </section>)}

      {hasAnalysisArtifacts && (<section className="analysis-stage detail-stage">
        <div className="stage-head static">
          <div className="stage-head-title">
            <div>
              <h3>Dossier d'analyse</h3>
              <p className="muted">{analysis ? `${analysis.document_labels.length} fichier(s) · ${imageCount} image(s) · ${findingsCount} constat(s)` : "Constats, données et plan extraits par l'IA."}</p>
            </div>
          </div>
        </div>
        <nav className="detail-tabs" aria-label="Categories d'analyse">
          {detailTabs.map((item) => (<button key={item.id} className={detailTab === item.id ? "active" : ""} onClick={() => setDetailTab(item.id)} type="button">
              {item.label}
              {!!item.count && <strong>{item.count}</strong>}
            </button>))}
        </nav>
        <div className="detail-content">
            {detailTab === "brief" && (<div className="folder-stack">
                <div className="section-heading">
                  <div className="muted">Contexte compact utilisé pour le plan et la rédaction.</div>
                  <button className="button secondary compact-button" onClick={onSave} disabled={busy}>
                    Enregistrer
                  </button>
                </div>
                <div className="field">
                  <label>Brief d'analyse</label>
                  <textarea rows={8} value={draft.analysis_brief} onChange={(event) => setDraft((current) => ({ ...current, analysis_brief: event.target.value }))}/>
                </div>
                <div className="field">
                  <label>Objectif</label>
                  <textarea rows={3} value={draft.report_intent.objective} onChange={(event) => setDraft((current) => ({
                ...current,
                objective: event.target.value,
                report_intent: { ...current.report_intent, objective: event.target.value }
            }))}/>
                </div>
                <div className="analysis-grid-2">
                  <div className="field">
                    <label>Attentes</label>
                    <textarea rows={5} value={draft.report_intent.expectations.join("\n")} onChange={(event) => setDraft((current) => ({
                ...current,
                report_intent: { ...current.report_intent, expectations: linesFromText(event.target.value) }
            }))}/>
                  </div>
                  <div className="field">
                    <label>Contraintes</label>
                    <textarea rows={5} value={draft.report_intent.constraints.join("\n")} onChange={(event) => setDraft((current) => ({
                ...current,
                report_intent: { ...current.report_intent, constraints: linesFromText(event.target.value) }
            }))}/>
                  </div>
                </div>
              </div>)}
            {detailTab === "sources" && (<div className="analysis-list">
                {!!(analysis?.source_tree ?? []).length && (<article className="source-inspector">
                    <div className="artifact-head">
                      <strong>Arborescence sources</strong>
                      <span>{analysis.source_tree.length} fichier(s)</span>
                    </div>
                    <ul className="analysis-simple-list compact">
                      {(analysis.source_tree ?? []).slice(0, 60).map((item) => (<li key={item.source_file_id || item.path}>{item.path || item.filename}</li>))}
                    </ul>
                  </article>)}
                {sourceRows.map((label) => (<article key={label.id} className="source-inspector">
                    <div className="artifact-head">
                      <button className="source-inspector-title" type="button" onClick={() => toggleSourcePreview(label)} disabled={!label.source_file_id}>
                        <strong>{label.filename}</strong>
                        <span>{openSourceId === label.source_file_id ? "Masquer la source" : "Voir la source"}</span>
                      </button>
                      <span>{label.document_type} - {label.status}</span>
                    </div>
                    {label.source_path && label.source_path !== label.filename && <span>{label.source_path}</span>}
                    <p>{label.description || "Description non disponible."}</p>
                    {label.relevance && <p>{label.relevance}</p>}
                    {!!label.key_results.length && (<ul className="analysis-simple-list compact">
                        {label.key_results.map((item, index) => <li key={`${label.id}-key-result-${index}`}>{item}</li>)}
                      </ul>)}
                    {!!label.limitations.length && <span>Limites: {label.limitations.join("; ")}</span>}
                    {openSourceId === label.source_file_id && (<SourcePreviewPanel projectId={projectId} sourceId={label.source_file_id} preview={sourcePreviews[label.source_file_id]} loading={loadingSourceId === label.source_file_id} error={sourcePreviewErrors[label.source_file_id]} />)}
                  </article>))}
                {!sourceRows.length && <div className="muted">Aucune source analysee pour le moment.</div>}
              </div>)}
            {detailTab === "findings" && (<div className="folder-stack">
                <div className="muted">
                  Findings consolidés pour guider le plan, les graphes et la rédaction.
                </div>
                <textarea rows={7} value={draft.report_brief || draft.knowledge_summary} onChange={(event) => setDraft((current) => ({
                ...current,
                report_brief: event.target.value,
                knowledge_summary: event.target.value
            }))}/>
                <div className="analysis-list">
                  {((analysis?.findings.length ? analysis.findings : analysis?.useful_facts) ?? []).map((fact) => (<article key={fact.id}>
                      <div className="artifact-head">
                        <strong>{fact.title}</strong>
                        <span>{fact.topic} - confiance {fact.confidence.toFixed(2)}</span>
                      </div>
                      <p>{fact.summary}</p>
                      {!!fact.evidence_ids.length && <span>{fact.evidence_ids.join(", ")}</span>}
                    </article>))}
                </div>
              </div>)}
            {detailTab === "data" && (<div className="folder-stack">
                <ul className="analysis-simple-list">
                  {(analysis?.data_insights ?? []).map((item, index) => <li key={`data-insight-${index}`}>{item}</li>)}
                </ul>
                <div className="analysis-list">
                  {((analysis?.generated_artifacts.length ? analysis.generated_artifacts : analysis?.data_artifacts) ?? []).map((artifact) => (<article key={artifact.id}>
                      <div className="artifact-head">
                        <strong>{artifact.title || artifact.path}</strong>
                        <span>{artifact.type}{artifact.section_id ? ` - ${artifact.section_id}` : ""}</span>
                      </div>
                      <p>{artifact.description}</p>
                      {artifact.path && <span>{artifact.path}</span>}
                      {!!artifact.source_paths?.length && <span>{artifact.source_paths.join("; ")}</span>}
                    </article>))}
                </div>
                <div className="asset-grid">
                  {charts.map((asset) => (<AssetPreview asset={asset} projectId={projectId} key={asset.id}/>))}
                </div>
              </div>)}
            {detailTab === "plan" && (<div className="folder-stack">
                <div className="analysis-list">
                  {visiblePlanSections.map((section) => (<article key={section.id}>
                      <div className="artifact-head">
                        <strong>{section.title}</strong>
                        <span>{section.role || "section"}{section.estimated_pages ? ` - ${section.estimated_pages} p.` : ""}</span>
                      </div>
                      {(section.key_message || section.brief) && <p>{section.key_message || section.brief}</p>}
                      {!!section.subsections?.length && <span>{section.subsections.slice(0, 5).join(" ; ")}</span>}
                      {!!section.data_questions?.length && <span>Data: {section.data_questions.slice(0, 3).join(" ; ")}</span>}
                      {!!section.visual_slots?.length && <span>Visuels: {section.visual_slots.slice(0, 3).map((slot) => slot.caption || slot.figure_path || slot.intent).filter(Boolean).join(" ; ")}</span>}
                    </article>))}
                  {!visiblePlanSections.length && <div className="muted">Le plan final sera généré après la curation automatique et tes choix facultatifs.</div>}
                </div>
                <ul className="analysis-simple-list">
                  {(analysis?.section_specs ?? []).map((spec) => (<li key={spec.id}>{spec.title}: {spec.role}</li>))}
                </ul>
                {analysis?.writing_blueprint && (<div className="analysis-list">
                    <article>
                      <div className="artifact-head">
                        <strong>Blueprint redactionnel</strong>
                        <span>{analysis.writing_blueprint.status} - {(analysis.writing_blueprint.content_blocks ?? []).length} bloc(s) - {(analysis.writing_blueprint.visual_slots ?? []).length} visuel(s)</span>
                      </div>
                      {analysis.writing_blueprint.summary && <p>{analysis.writing_blueprint.summary}</p>}
                    </article>
                    {(analysis.writing_blueprint.content_blocks ?? []).slice(0, 30).map((block) => (<article key={block.id}>
                        <div className="artifact-head">
                          <strong>{block.section_title || block.section_id}</strong>
                          <span>{block.block_title || block.editorial_role}</span>
                        </div>
                        <p>{block.intent}</p>
                        {!!(block.key_results ?? []).length && <span>{block.key_results.join(" ; ")}</span>}
                      </article>))}
                    {!!(analysis.writing_blueprint.visual_slots ?? []).length && (<article>
                        <div className="artifact-head">
                          <strong>Slots visuels</strong>
                          <span>Placement prevu par la passe editoriale</span>
                        </div>
                        <ul className="analysis-simple-list compact">
                          {(analysis.writing_blueprint.visual_slots ?? []).slice(0, 40).map((slot) => (<li key={slot.id}>{slot.section_id}{slot.block_title ? ` - ${slot.block_title}` : ""}: {slot.caption || slot.figure_path || slot.creation_hint} {slot.status ? `(${slot.status})` : ""}</li>))}
                        </ul>
                      </article>)}
                  </div>)}
              </div>)}
            {detailTab === "runs" && (<div className="folder-stack">
                <div className="analysis-grid-2">
                  <div className="field">
                    <label>Questions bloquantes</label>
                    <textarea rows={8} value={draft.blocking_questions.join("\n")} onChange={(event) => setDraft((current) => ({ ...current, blocking_questions: linesFromText(event.target.value) }))}/>
                  </div>
                  <div className="field">
                    <label>Questions ouvertes</label>
                    <textarea rows={8} value={draft.open_questions.join("\n")} onChange={(event) => setDraft((current) => ({ ...current, open_questions: linesFromText(event.target.value) }))}/>
                  </div>
                </div>
                <ul className="analysis-simple-list">
                  {(analysis?.analysis_tasks ?? []).slice(-30).map((task) => (<li key={task.id}>Passe {task.pass_index || "-"} - {task.action} - {task.status}: {task.summary}</li>))}
                  {(analysis?.agent_runs ?? []).slice(-30).map((run) => (<li key={run.id}>{run.agent_name} - {run.intelligence} - {run.status}: {run.summary}</li>))}
                </ul>
                <ul className="analysis-simple-list">
                  {(analysis?.journal ?? []).slice(-30).map((item, index) => (<li key={`${item}-${index}`}>{item}</li>))}
                </ul>
              </div>)}
        </div>
      </section>)}
    </div>);
}

function ReportContextPanel({ project, projectId, contextDraft, onContextChange, onSave, saving, uploadBusy, onUploadFiles, pendingQuestions, answeredQuestions, onOpenQuestion, questionVisuals, onClose }) {
    const integrationStatus = project.context_integration_status || "idle";
    const integrationLabel = integrationStatus === "running"
        ? "Integration en cours"
        : integrationStatus === "pending"
            ? "Integration en attente"
            : integrationStatus === "failed"
                ? "Integration a relancer"
                : "Contexte a jour";
    const answerLabel = (question) => Array.isArray(question.answer)
        ? question.answer.join(" ; ")
        : String(question.answer || (question.status === "dismissed" ? "Question passee" : ""));
    const unsavedContext = String(contextDraft || "") !== String(project.user_context || "");
    const effectiveRevision = project.context_history?.length
        ? project.context_history[project.context_history.length - 1]
        : null;
    return (<div className="report-context-backdrop" onMouseDown={(event) => {
        if (event.target === event.currentTarget)
            onClose();
    }}>
      <aside className="report-context-panel" aria-label="Contexte du rapport">
        <div className="report-context-head">
          <div>
            <span className={`context-integration-status ${integrationStatus}`}>{integrationLabel}</span>
            <h2>Contexte du rapport</h2>
            <p>Ajoute des informations ou des sources sans interrompre ton travail.</p>
          </div>
          <button className="icon-button button secondary" type="button" onClick={onClose} title="Fermer">
            <X size={17}/>
          </button>
        </div>

        <section className="report-context-section">
          <div className="section-heading">
            <h3>Informations et consignes</h3>
            <button className="button compact-button" type="button" disabled={saving} onClick={onSave}>
              <CheckCircle size={15}/> {saving ? "Enregistrement" : "Enregistrer et integrer"}
            </button>
          </div>
          <textarea rows={7} value={contextDraft} onChange={(event) => onContextChange(event.target.value)} placeholder="Ajoute ici une information, une contrainte ou une consigne qui t'est venue apres le lancement..."/>
          {unsavedContext ? (
            <p className="context-effective-warning">Cette version n'est pas encore le brief effectif. Clique sur « Enregistrer et integrer » avant de relancer l'analyse.</p>
          ) : (
            <p className="muted">Brief effectif : revision {effectiveRevision?.revision ?? project.context_revision ?? 0}{project.effective_context_fingerprint ? ` · ${project.effective_context_fingerprint}` : ""}.</p>
          )}
          <p className="muted">Le texte reste modifiable. Guttenberg analyse uniquement la mise a jour et cible les parties concernees.</p>
        </section>

        <section className="report-context-section">
          <div className="section-heading">
            <h3>Documents et photos</h3>
            <span className="muted">{project.source_files?.length || 0} source(s)</span>
          </div>
          <label className="dropzone row">
            <Upload size={18}/>
            <span>{uploadBusy ? "Ajout en cours..." : "Ajouter des fichiers a tout moment"}</span>
            <input hidden type="file" multiple disabled={uploadBusy} onChange={onUploadFiles}/>
          </label>
          {!!project.pending_context_source_ids?.length && <p className="muted">{project.pending_context_source_ids.length} source(s) en attente d'integration.</p>}
          <ul className="file-list compact-file-list context-source-list">
            {(project.source_files ?? []).slice(-8).reverse().map((source) => (<li key={source.id}>
                <strong>{source.filename}</strong>
                <span className="muted">{source.parsed ? "Analysee" : "En attente d'analyse"}</span>
              </li>))}
          </ul>
        </section>

        <section className="report-context-section">
          <div className="section-heading">
            <h3>Questions Guttenberg</h3>
            <span className="muted">{pendingQuestions.length} en attente</span>
          </div>
          <div className="context-question-list">
            {pendingQuestions.map((question) => (<ContextQuestionCard key={question.id} question={question} projectId={projectId} visuals={questionVisuals(question)} actionLabel="Repondre" onOpen={() => onOpenQuestion(question)}/>))}
            {answeredQuestions.map((question) => (<ContextQuestionCard key={question.id} question={question} projectId={projectId} visuals={questionVisuals(question)} answer={answerLabel(question)} actionLabel="Modifier ma reponse" onOpen={() => onOpenQuestion(question)}/>))}
            {!pendingQuestions.length && !answeredQuestions.length && <p className="muted">Aucune question posee pour le moment.</p>}
          </div>
        </section>
      </aside>
    </div>);
}

function ContextQuestionCard({ question, projectId, visuals, answer = "", actionLabel, onOpen }) {
    return (<article className={`context-question-card ${question.status}`}>
      <div>
        <span>{question.status === "pending" ? question.blocking ? "Reponse requise" : "En attente" : "Repondue"}</span>
        <strong>{question.prompt}</strong>
      </div>
      {!!visuals.length && (<div className="context-question-visuals">
          {visuals.slice(0, 3).map((asset) => <img key={asset.id} src={reportImageSrc(projectId, asset.path)} alt={asset.user_label || asset.caption || asset.title || "Visuel concerne"}/>)}
        </div>)}
      {answer && <blockquote>{answer}</blockquote>}
      {!!question.source_file_ids?.length && <small>{question.source_file_ids.length} piece(s) jointe(s)</small>}
      <button className="button secondary compact-button" type="button" onClick={onOpen}>{actionLabel}</button>
    </article>);
}

export function ClarificationQuestionModal({ question, pendingCount, questionPosition = 1, hasNextQuestion = false, busy, onAnswer, onClose, onNext, projectId = "", referencedVisuals = [], availableVisuals = [], attachedSources = [] }) {
    const options = question.options ?? [];
    const recommended = options.find((option) => option.recommended) ?? options[0];
    const hasSavedAnswer = ["answered", "dismissed"].includes(question.status);
    const savedAnswer = hasSavedAnswer ? question.answer : "";
    const defaultValue = savedAnswer || question.default_answer || recommended?.value || recommended?.label || "";
    const initialSelectedValues = Array.isArray(defaultValue) ? defaultValue : defaultValue ? [defaultValue] : [];
    const [textAnswer, setTextAnswer] = useState(options.length ? "" : String(Array.isArray(defaultValue) ? defaultValue.join("; ") : defaultValue));
    const [selectedValues, setSelectedValues] = useState(initialSelectedValues);
    const [showCustomAnswer, setShowCustomAnswer] = useState(false);
    const [attachmentFiles, setAttachmentFiles] = useState([]);
    const [selectedBankVisualIds, setSelectedBankVisualIds] = useState(question.selected_visual_asset_ids ?? []);
    const [bankSearch, setBankSearch] = useState("");
    const [bankVisibleCount, setBankVisibleCount] = useState(30);
    useEffect(() => {
        const nextOptions = question.options ?? [];
        const nextRecommended = nextOptions.find((option) => option.recommended) ?? nextOptions[0];
        const nextSaved = ["answered", "dismissed"].includes(question.status) ? question.answer : "";
        const nextDefault = nextSaved || question.default_answer || nextRecommended?.value || nextRecommended?.label || "";
        const nextValues = Array.isArray(nextDefault) ? nextDefault : nextDefault ? [nextDefault] : [];
        setTextAnswer(nextOptions.length ? "" : String(Array.isArray(nextDefault) ? nextDefault.join("; ") : nextDefault));
        setSelectedValues(nextValues);
        setShowCustomAnswer(Boolean(nextOptions.length && nextSaved && !nextValues.some((value) => nextOptions.some((option) => optionValue(option) === value))));
        setAttachmentFiles([]);
        setSelectedBankVisualIds(question.selected_visual_asset_ids ?? []);
        setBankSearch("");
        setBankVisibleCount(30);
        if (nextOptions.length && nextSaved && !nextValues.some((value) => nextOptions.some((option) => optionValue(option) === value)))
            setTextAnswer(String(Array.isArray(nextSaved) ? nextSaved.join("; ") : nextSaved));
        // optionValue is stable for the lifetime of this component.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [question]);
    const isMultiple = question.kind === "multiple_choice";
    const hasOptions = options.length > 0;
    const allowCustomAnswer = question.allow_custom_answer !== false;
    const allowImageBankSelection = Boolean(question.allow_image_bank_selection || question.visual_selection_mode === "bank");
    const filteredBankVisuals = useMemo(() => {
        const excluded = new Set(question.excluded_visual_asset_ids ?? []);
        const needle = bankSearch.trim().toLocaleLowerCase();
        if (!needle)
            return availableVisuals.filter((asset) => !excluded.has(asset.id));
        return availableVisuals.filter((asset) => !excluded.has(asset.id) && [asset.user_label, asset.title, asset.proposed_caption, asset.caption, asset.source_filename, asset.source_path, asset.visual_summary]
            .filter(Boolean)
            .join(" ")
            .toLocaleLowerCase()
            .includes(needle));
    }, [availableVisuals, bankSearch, question.excluded_visual_asset_ids]);
    function optionValue(option) {
        return option.value || option.label;
    }
    function toggleOption(option) {
        const value = optionValue(option);
        setSelectedValues((current) => current.includes(value)
            ? current.filter((item) => item !== value)
            : [...current, value]);
    }
    function submitText(event) {
        event.preventDefault();
        submitAnswer(textAnswer || question.default_answer || "Non precise.");
    }
    function submitAnswer(value, dismissed = false, visualAssetIds = []) {
        if (visualAssetIds.length) {
            onAnswer(value, dismissed, attachmentFiles, visualAssetIds);
        }
        else if (attachmentFiles.length)
            onAnswer(value, dismissed, attachmentFiles);
        else if (dismissed)
            onAnswer(value, true);
        else
            onAnswer(value);
    }
    function visualIdsForValues(values) {
        const selected = new Set(Array.isArray(values) ? values : [values]);
        return options
            .filter((option) => option.visual_asset_id && selected.has(optionValue(option)))
            .map((option) => option.visual_asset_id);
    }
    const editingAnswer = ["answered", "dismissed"].includes(question.status);
    return (<div className="clarification-backdrop" role="dialog" aria-modal="true" aria-labelledby="clarification-title">
      <section className={`clarification-modal ${allowImageBankSelection ? "with-image-bank" : ""}`}>
        <div className="clarification-head">
          <div>
            <span>{editingAnswer ? "Reponse deja enregistree" : question.blocking ? "Reponse requise pour poursuivre" : pendingCount > 1 ? `Precision ${questionPosition}/${pendingCount}` : "Precision optionnelle"}</span>
            <h3 id="clarification-title">{question.title || "Precision utile au rapport"}</h3>
          </div>
          <button className="icon-button button secondary" type="button" onClick={onClose} title="Fermer">
            <X size={16}/>
          </button>
        </div>
        <p className="clarification-question">{question.prompt}</p>
        {question.rationale && <p className="clarification-rationale">{question.rationale}</p>}
        {question.helper_text && <p className="muted">{question.helper_text}</p>}
        {!!referencedVisuals.length && (<div className="clarification-visuals">
            {referencedVisuals.map((asset) => (<figure key={asset.id}>
                <img src={assetThumbnailSrc(projectId, asset.path, 640)} alt={asset.user_label || asset.proposed_caption || asset.caption || asset.title || "Visuel concerne"}/>
                <figcaption>{asset.user_label || asset.proposed_caption || asset.caption || asset.title || asset.source_filename || "Visuel concerne"}</figcaption>
              </figure>))}
          </div>)}
        {hasOptions ? (<div className="clarification-options">
            {options.map((option) => {
                const value = optionValue(option);
                const active = selectedValues.includes(value);
                const optionAsset = option.visual_asset_id
                    ? availableVisuals.find((asset) => asset.id === option.visual_asset_id)
                        || referencedVisuals.find((asset) => asset.id === option.visual_asset_id)
                    : null;
                return (<button
                    key={option.id || value}
                    className={`clarification-option ${active ? "active" : ""}`}
                    type="button"
                    disabled={busy}
                    onClick={() => isMultiple ? toggleOption(option) : submitAnswer(value, false, option.visual_asset_id ? [option.visual_asset_id] : [])}
                  >
                    {optionAsset && <img className="clarification-option-thumbnail" src={assetThumbnailSrc(projectId, optionAsset.path, 360)} alt={optionAsset.user_label || optionAsset.title || option.label}/>}
                    <span className="clarification-option-copy">
                      <strong>{option.label}</strong>
                      {option.description && <span>{option.description}</span>}
                    </span>
                    {option.recommended && <small>Recommande</small>}
                  </button>);
            })}
            {isMultiple && hasOptions && (<button className="button" type="button" disabled={busy || !selectedValues.length} onClick={() => submitAnswer(selectedValues, false, visualIdsForValues(selectedValues))}>
                <CheckCircle size={16}/> Valider
              </button>)}
            {allowCustomAnswer && (<button className="button secondary compact-button clarification-custom-toggle" type="button" disabled={busy} onClick={() => setShowCustomAnswer((current) => !current)}>
                {showCustomAnswer ? "Masquer la reponse libre" : "Autre reponse..."}
              </button>)}
          </div>) : null}
        {allowImageBankSelection && (<section className="clarification-image-bank">
            <div className="clarification-image-bank-head">
              <strong>Choisir dans la banque d'images</strong>
              <input type="search" value={bankSearch} onChange={(event) => {
                setBankSearch(event.target.value);
                setBankVisibleCount(30);
              }} placeholder="Rechercher par document ou legende"/>
            </div>
            <div className="clarification-image-bank-grid">
              {filteredBankVisuals.slice(0, bankVisibleCount).map((asset) => {
                const selected = selectedBankVisualIds.includes(asset.id);
                return (<button key={asset.id} type="button" className={`clarification-bank-asset ${selected ? "active" : ""}`} disabled={busy} onClick={() => setSelectedBankVisualIds((current) => current.includes(asset.id)
                    ? current.filter((id) => id !== asset.id)
                    : isMultiple ? [...current, asset.id] : [asset.id])}>
                    <img src={assetThumbnailSrc(projectId, asset.path, 360)} alt={asset.user_label || asset.title || asset.source_filename || "Visuel"}/>
                    <span>{asset.user_label || asset.title || asset.proposed_caption || asset.source_filename || "Visuel"}</span>
                    {asset.source_filename && <small>{asset.source_filename}</small>}
                  </button>);
              })}
            </div>
            {filteredBankVisuals.length > bankVisibleCount && (<button className="button secondary compact-button" type="button" onClick={() => setBankVisibleCount((current) => current + 30)}>
                Afficher 30 visuels de plus
              </button>)}
            <button className="button" type="button" disabled={busy || !selectedBankVisualIds.length} onClick={() => {
              const labels = selectedBankVisualIds.map((id) => {
                const asset = availableVisuals.find((item) => item.id === id);
                return asset?.user_label || asset?.title || asset?.source_filename || id;
              });
              submitAnswer(`Visuel(s) choisi(s): ${labels.join(", ")}`, false, selectedBankVisualIds);
            }}>
              <CheckCircle size={16}/> Valider {selectedBankVisualIds.length ? `(${selectedBankVisualIds.length})` : ""}
            </button>
          </section>)}
        {((!hasOptions && !allowImageBankSelection) || showCustomAnswer) && (<form className="clarification-form" onSubmit={submitText}>
            <textarea rows={4} value={textAnswer} onChange={(event) => setTextAnswer(event.target.value)} placeholder="Reponse ou precision courte"/>
            <button className="button" type="submit" disabled={busy || !textAnswer.trim()}>
              <CheckCircle size={16}/> {editingAnswer ? "Enregistrer la modification" : "Valider"}
            </button>
          </form>)}
        <div className="clarification-attachments">
          <label className="dropzone compact-dropzone">
            <Upload size={16}/>
            <span>Joindre un document ou une photo</span>
            <input hidden type="file" multiple onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              if (files.length)
                  setAttachmentFiles((current) => [...current, ...files]);
              event.target.value = "";
          }}/>
          </label>
          {!!attachedSources.length && (<div className="attachment-chips">
              {attachedSources.map((source) => <span className="file" key={source.id}>Deja joint: {source.filename}</span>)}
            </div>)}
          {!!attachmentFiles.length && (<div className="attachment-chips">
              {attachmentFiles.map((file, index) => (<button className="file" key={`${file.name}-${index}`} type="button" onClick={() => setAttachmentFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))}>
                  {file.name}<X size={13}/>
                </button>))}
            </div>)}
        </div>
        <div className="clarification-footer">
          {!editingAnswer && !question.blocking && (<button className="button secondary compact-button" type="button" disabled={busy} onClick={() => submitAnswer("", true)}>
              {question.default_answer ? "Continuer avec l'option recommandee" : "Continuer sans repondre"}
            </button>)}
          {!editingAnswer && question.blocking && <span className="clarification-blocking-note">Une reponse est necessaire pour reprendre l'analyse.</span>}
          {pendingCount > 1 && (<button className="button secondary compact-button" type="button" disabled={busy} onClick={onNext}>
              {hasNextQuestion ? "Question suivante" : "Fermer la revue"}
            </button>)}
        </div>
      </section>
    </div>);
}

function SourcePreviewPanel({ projectId, sourceId, preview, loading, error }) {
    if (loading) {
        return <div className="source-preview-panel muted">Chargement de la source...</div>;
    }
    if (error) {
        return <div className="source-preview-panel source-preview-error">{error}</div>;
    }
    if (!preview) {
        return <div className="source-preview-panel muted">Apercu en attente.</div>;
    }
    const fileUrl = api.sourceFileUrl(projectId, sourceId);
    const previewPath = preview.source_path || sourcePathLabel(preview.source || {});
    if (preview.kind === "pdf") {
        return (<div className="source-preview-panel">
          <div className="source-preview-toolbar">
            <span>{preview.content_type || "PDF"}</span>
            {previewPath && previewPath !== preview.filename && <span>{previewPath}</span>}
            <a href={fileUrl} target="_blank" rel="noreferrer">Ouvrir dans un onglet</a>
          </div>
          <iframe className="source-preview-frame" src={fileUrl} title={`Source ${preview.filename}`}/>
        </div>);
    }
    if (preview.kind === "image") {
        return (<div className="source-preview-panel">
          <div className="source-preview-toolbar">
            <span>{preview.content_type || "Image"}</span>
            {previewPath && previewPath !== preview.filename && <span>{previewPath}</span>}
            <a href={fileUrl} target="_blank" rel="noreferrer">Ouvrir dans un onglet</a>
          </div>
          <img className="source-preview-image" src={fileUrl} alt={preview.filename}/>
        </div>);
    }
    if (preview.kind === "text") {
        return (<div className="source-preview-panel">
          <div className="source-preview-toolbar">
            <span>{preview.content_type || "Texte"}</span>
            {previewPath && previewPath !== preview.filename && <span>{previewPath}</span>}
            {preview.truncated && <span>Extrait limite aux premiers caracteres.</span>}
            <a href={fileUrl} target="_blank" rel="noreferrer">Ouvrir le fichier</a>
          </div>
          <pre className="source-preview-text">{preview.content || ""}</pre>
        </div>);
    }
    return (<div className="source-preview-panel">
      <div className="source-preview-toolbar">
        <span>Preview non disponible pour ce type de fichier.</span>
        {previewPath && previewPath !== preview.filename && <span>{previewPath}</span>}
        <a href={fileUrl} target="_blank" rel="noreferrer">Ouvrir le fichier</a>
      </div>
    </div>);
}

function ImageBankPanel({ report, analysis, projectId, selectedImage, onSelectImage, onDiscussImage, assistantImages = [], onToggleAssistantImage, onOpenAssistant }) {
    const assetsByPath = new Map((analysis?.visual_assets ?? []).map((asset) => [asset.path, asset]));
    const assistantPaths = new Set(assistantImages.map((image) => image.path));
    const usage = reportFigureUsage(report.sections);
    const byPath = new Map();
    report.manifest.figures.forEach((figure) => {
        const asset = assetsByPath.get(figure.path);
        byPath.set(figure.path, {
            id: figure.id,
            path: figure.path,
            caption: figure.caption,
            status: asset?.status ?? "banque",
            type: asset?.type ?? figure.generated_or_original ?? "image",
            summary: asset?.visual_summary || asset?.summary || "",
            source_path: asset?.source_path || "",
            created_at: asset?.created_at || figure.created_at || ""
        });
    });
    (analysis?.visual_assets ?? [])
        .filter((asset) => asset.path && asset.type !== "chart")
        .forEach((asset) => {
        if (byPath.has(asset.path))
            return;
        byPath.set(asset.path, {
            id: asset.id,
            path: asset.path,
            caption: asset.caption || asset.title || asset.path,
            status: asset.status,
            type: asset.type,
            summary: asset.visual_summary || asset.summary || "",
            source_path: asset.source_path || "",
            created_at: asset.created_at || ""
        });
    });
    const images = [...byPath.values()];
    return (<section className="panel image-bank-panel">
      <div className="section-heading">
        <h2>Banque d'images</h2>
        <span className="muted">{images.length}</span>
      </div>
      {selectedImage && (<div className="bank-selection">
          <span>Selection: {selectedImage.caption || selectedImage.path}</span>
          <button className="button secondary compact-button" type="button" onClick={() => onSelectImage(null)}>
            Annuler
          </button>
        </div>)}
      {!!assistantImages.length && (<div className="bank-assistant-selection">
          <span>{assistantImages.length} image(s) dans le message Assistant</span>
          <button className="button compact-button" type="button" onClick={onOpenAssistant}>
            <MessageSquare size={14}/> Ouvrir l'Assistant
          </button>
        </div>)}
      <div className="image-bank-list">
        {images.map((image) => {
            const selected = selectedImage?.path === image.path;
            const assistantSelected = assistantPaths.has(image.path);
            const usedIn = usage.get(image.path) ?? [];
            return (<article className={`${selected ? "selected" : ""} ${assistantSelected ? "assistant-selected" : ""}`.trim()} key={image.path}>
              <img
                src={assetThumbnailSrc(projectId, image.path, 192, image.created_at || image.id)}
                alt={image.caption || image.path}
                loading="lazy"
                decoding="async"
                width="92"
                height="78"
              />
              <div>
                <strong>{image.caption || image.path}</strong>
                <span>{image.type} - {image.status}{usedIn.length ? ` - ${usedIn.join(", ")}` : ""}</span>
                {image.source_path && <span>{image.source_path}</span>}
                {image.summary && <p>{image.summary}</p>}
                <div className="row">
                  {onToggleAssistantImage ? (<button className={`button compact-button ${assistantSelected ? "" : "secondary"}`} type="button" onClick={() => onToggleAssistantImage(image)}>
                      {assistantSelected ? <CheckCircle size={14}/> : <Plus size={14}/>}
                      {assistantSelected ? "Dans le message" : "Ajouter au message"}
                    </button>) : (<button className="button secondary compact-button" type="button" onClick={() => onDiscussImage(image)}>
                      <MessageSquare size={14}/> Discuter
                    </button>)}
                  <button className="button compact-button" type="button" onClick={() => onSelectImage(selected ? null : { path: image.path, caption: image.caption })}>
                    {selected ? "Selectionnee" : "Selectionner"}
                  </button>
                </div>
              </div>
            </article>);
        })}
        {!images.length && <div className="muted">Les images détectées ou générées après analyse apparaitront ici.</div>}
      </div>
    </section>);
}

function reportFigureUsage(sections) {
    const usage = new Map();
    Object.entries(sections).forEach(([sectionId, markdown]) => {
        markdownImageRefs(markdown).forEach((image) => {
            const list = usage.get(image.path) ?? [];
            if (!list.includes(sectionId))
                list.push(sectionId);
            usage.set(image.path, list);
        });
    });
    return usage;
}

function AssetPreview({ asset, projectId, onStatusChange, onRequestSourceRejection, onLabelChange, locked = false }) {
    const [labelDraft, setLabelDraft] = useState(asset.user_label ?? "");
    const [savingLabel, setSavingLabel] = useState(false);
    const [savingStatus, setSavingStatus] = useState(false);
    const [rejectPressActive, setRejectPressActive] = useState(false);
    const labelDirtyRef = useRef(false);
    const rejectPressTimerRef = useRef(null);
    const rejectLongPressRef = useRef(false);
    const filename = asset.path.split("/").pop() ?? asset.path;
    const sectionId = asset.spec["section_id"];
    const scriptPath = asset.spec["script"];
    const sourceContextType = sourceContextDocumentType(asset.spec["source_context"]);
    useEffect(() => {
        if (!labelDirtyRef.current) {
            setLabelDraft(asset.user_label ?? "");
        }
    }, [asset.user_label, asset.id]);
    useEffect(() => {
        if (!onLabelChange || locked || !labelDirtyRef.current)
            return;
        const nextLabel = labelDraft.trim();
        if (nextLabel === (asset.user_label ?? "").trim())
            return;
        const timer = window.setTimeout(async () => {
            setSavingLabel(true);
            try {
                await onLabelChange(nextLabel);
                labelDirtyRef.current = false;
            }
            finally {
                setSavingLabel(false);
            }
        }, 700);
        return () => window.clearTimeout(timer);
    }, [asset.user_label, labelDraft, locked, onLabelChange]);
    useEffect(() => () => {
        if (rejectPressTimerRef.current)
            window.clearTimeout(rejectPressTimerRef.current);
    }, []);
    async function changeStatus(status) {
        if (savingStatus || locked)
            return;
        setSavingStatus(true);
        try {
            await onStatusChange(status);
        }
        catch (_error) {
            // The parent restores the previous asset and displays the API error.
        }
        finally {
            setSavingStatus(false);
        }
    }
    function beginRejectPress(event) {
        if (savingStatus || locked || asset.status === "rejected" || !onRequestSourceRejection)
            return;
        event.currentTarget.setPointerCapture?.(event.pointerId);
        rejectLongPressRef.current = false;
        setRejectPressActive(true);
        if (rejectPressTimerRef.current)
            window.clearTimeout(rejectPressTimerRef.current);
        rejectPressTimerRef.current = window.setTimeout(() => {
            rejectPressTimerRef.current = null;
            rejectLongPressRef.current = true;
            setRejectPressActive(false);
            Promise.resolve(onRequestSourceRejection()).catch(() => {});
        }, 650);
    }
    function endRejectPress(event) {
        if (rejectPressTimerRef.current) {
            window.clearTimeout(rejectPressTimerRef.current);
            rejectPressTimerRef.current = null;
        }
        setRejectPressActive(false);
        if (event.currentTarget.hasPointerCapture?.(event.pointerId))
            event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
    function clickReject(event) {
        if (rejectLongPressRef.current) {
            rejectLongPressRef.current = false;
            event.preventDefault();
            return;
        }
        changeStatus("rejected");
    }
    return (<article className={`asset-preview ${asset.status} ${locked ? "locked" : ""}`}>
      <div className="asset-media">
        {asset.path && (<img
          src={api.assetThumbnailUrl(projectId, filename, 480, asset.created_at || asset.id)}
          alt={asset.caption || asset.title}
          loading="lazy"
          decoding="async"
          width="480"
          height="300"
        />)}
        {onStatusChange && (<div className="asset-actions" aria-label="Actions image">
            <button className="asset-action-button" disabled={locked || savingStatus || asset.status === "required"} onClick={() => changeStatus("required")} title="À utiliser dans le rapport" aria-label="À utiliser dans le rapport" type="button">
              <BookmarkPlus size={15}/>
            </button>
            <button className="asset-action-button" disabled={locked || savingStatus || asset.status === "accepted"} onClick={() => changeStatus("accepted")} title="Préférer cette image sans la rendre obligatoire" aria-label="Préférer cette image" type="button">
              <CheckCircle size={15}/>
            </button>
            <button className={`asset-action-button danger ${rejectPressActive ? "pressing" : ""}`} disabled={locked || savingStatus || asset.status === "rejected"} onPointerDown={beginRejectPress} onPointerUp={endRejectPress} onPointerCancel={endRejectPress} onClick={clickReject} onContextMenu={(event) => event.preventDefault()} title="Écarter cette image · maintenir pour écarter tout le document" aria-label="Écarter cette image" type="button">
              <X size={15}/>
            </button>
          </div>)}
      </div>
      <div className="asset-body">
        <strong>{asset.caption || asset.title}</strong>
        <span>
          {asset.type} - {asset.editorial_status || asset.status} - score {asset.quality_score.toFixed(2)}
          {asset.vision_confidence ? ` - vision ${asset.vision_confidence.toFixed(2)}` : ""}
        </span>
        {asset.source_filename && (<span>Source: {asset.source_filename}{asset.page_number ? `, page ${asset.page_number}` : ""}</span>)}
        {asset.source_path && asset.source_path !== asset.source_filename && (<span>Chemin: {asset.source_path}</span>)}
        <div className="asset-detail-scroll">
          {sourceContextType && <span>Contexte source: {sourceContextType}</span>}
          {typeof sectionId === "string" && sectionId && <span>Cible: {sectionId}</span>}
          {typeof scriptPath === "string" && scriptPath && <span>Script: {scriptPath}</span>}
          {asset.visual_summary && <p>{asset.visual_summary}</p>}
          {asset.visible_text && <p>Texte visible: {asset.visible_text}</p>}
          {!!asset.content_tags.length && <span>{asset.content_tags.slice(0, 6).join(", ")}</span>}
          {asset.rejection_reason && <p>{asset.rejection_reason}</p>}
        </div>
        {onLabelChange && (<div className="asset-user-label">
            <label>Indication pour Guttenberg</label>
            <textarea rows={2} value={labelDraft} onChange={(event) => {
                labelDirtyRef.current = true;
                setLabelDraft(event.target.value);
            }} placeholder="Décris l'image ou précise ce qu'elle doit montrer au rapport" disabled={locked}/>
            <span className="asset-note-status">
              {locked
                ? "Verrouillé pendant l'analyse"
                : savingLabel
                  ? "Enregistrement..."
                  : "Cette indication guide l'IA ; la légende du rapport sera reformulée."}
            </span>
          </div>)}
      </div>
    </article>);
}

export {
    AssetPreview,
    ContextQuestionCard,
    ImageBankPanel,
    ReportContextPanel,
    SourcePreviewPanel,
    reportFigureUsage
};
